/**
 * ClaimLeftSidebarContent
 *
 * Tab-switcher for the claim detail left sidebar. Provides a SegmentedControl
 * to toggle between "Patient" (PatientInformationView) and "Billing Codes"
 * (reuses WorkingBillingClaimEditor from EncounterDetail).
 */

import React, { useMemo, useState } from 'react';
import { SegmentedControl } from '@components/primitives/SegmentedControl';
import { Box, Stack, Typography } from '@mui/material';

import PatientInformationView from '../../../../components/Patient/PatientInformationView';
import { WorkingBillingClaimEditor } from '../EncounterDetail/WorkingBillingClaimEditor';

import { ClaimRebuildButton } from './ClaimRebuildButton';

type SidebarTab = 'patient' | 'billing';

interface ClaimLeftSidebarContentProps {
  patientId?: string | null;
  encounterId?: string;
  claimId: string;
  claimStatus?: string;
  onRebuildStart?: () => void;
}

export const ClaimLeftSidebarContent: React.FC<ClaimLeftSidebarContentProps> = ({
  patientId,
  encounterId,
  claimId,
  claimStatus,
  onRebuildStart,
}) => {
  const hasPatient = Boolean(patientId);

  // Tab options — hide Patient tab when no patient ID
  const tabOptions = useMemo(() => {
    const options = [];
    if (hasPatient) {
      options.push({ value: 'patient', label: 'Patient' });
    }
    options.push({ value: 'billing', label: 'Billing Codes' });
    return options;
  }, [hasPatient]);

  const [activeTab, setActiveTab] = useState<SidebarTab>(hasPatient ? 'patient' : 'billing');

  const showRebuild = claimStatus === 'draft' || claimStatus === 'ready';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Tab switcher — only show if both tabs are available */}
      {tabOptions.length > 1 && (
        <Box sx={{ px: 2, pt: 2, pb: 1, flexShrink: 0 }}>
          <SegmentedControl
            options={tabOptions}
            value={activeTab}
            onChange={(val) => setActiveTab(val as SidebarTab)}
            size="small"
          />
        </Box>
      )}

      {/* Content */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {activeTab === 'patient' && patientId && (
          <PatientInformationView patientId={patientId} />
        )}
        {activeTab === 'billing' && (
          <>
            {encounterId ? (
              <WorkingBillingClaimEditor encounterId={encounterId} />
            ) : (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="body2" color="text.disabled">
                  No encounter linked to this claim.
                </Typography>
              </Box>
            )}

            {/* Rebuild Button */}
            {showRebuild && encounterId && (
              <Box sx={{ px: 2, pb: 2, pt: 1, borderTop: 1, borderColor: 'divider' }}>
                <ClaimRebuildButton
                  claimId={claimId}
                  claimStatus={claimStatus}
                  onRebuildStart={onRebuildStart}
                />
              </Box>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};
